import { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { User, ShoppingBag, Heart, Bell, MoreHorizontal, Home, LogOut, MessageSquare, Settings, Image, Users, HelpCircle, Wrench, Megaphone, Palette, Mail, Send, MessageCircle, Search, Bot, Monitor, FileText, Accessibility } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { useStaticTranslation } from "@/lib/staticTranslations";

type TabType = "details" | "orders" | "favorites" | "repairs" | "messages" | "admin" | "settings" | "slider" | "users" | "faq" | "admin-repairs" | "promo-banner" | "color-helper" | "customer-messages" | "sms-templates" | "search-settings" | "bot-settings" | "tv-display" | "blog-management" | "accessibility";

interface MobileProfileNavProps {
  activeTab: TabType;
  onTabChange: (tab: TabType) => void;
  isAdmin: boolean;
  newMessagesCount: number;
  newRepairsCount: number;
  unreadUserMessagesCount: number;
  onLogout: () => void;
  firstName?: string;
}

export const MobileProfileNav = ({
  activeTab,
  onTabChange,
  isAdmin,
  newMessagesCount,
  newRepairsCount,
  unreadUserMessagesCount,
  onLogout,
  firstName,
}: MobileProfileNavProps) => {
  const [moreOpen, setMoreOpen] = useState(false);
  const { t: at, isHebrew } = useStaticTranslation("admin");

  // Main bottom nav items (4 items + more)
  const mainNavItems = [
    { id: "details" as TabType, label: at("main", "ראשי"), icon: Home },
    { id: "orders" as TabType, label: at("orders", "הזמנות"), icon: ShoppingBag },
    { id: "favorites" as TabType, label: at("favorites", "מועדפים"), icon: Heart },
    { id: "repairs" as TabType, label: at("tracking", "מעקב"), icon: Bell, hasBadge: true },
  ];

  const handleTabClick = (tab: TabType) => {
    onTabChange(tab);
  };

  const handleMoreItemClick = (tab: TabType) => {
    onTabChange(tab);
    setMoreOpen(false);
  };

  // Additional items for "More" drawer
  const moreNavItems = [
    { id: "messages" as TabType, label: at("myMessages", "ההודעות שלי"), icon: Mail, badge: unreadUserMessagesCount },
  ];

  // Admin items
  const adminCommunicationItems = isAdmin ? [
    { id: "admin" as TabType, label: at("contactMessages", "הודעות צור קשר"), icon: MessageSquare, badge: newMessagesCount },
    { id: "customer-messages" as TabType, label: at("sendMessages", "שליחת הודעות"), icon: Send },
    { id: "sms-templates" as TabType, label: at("smsMessages", "הודעות SMS"), icon: MessageCircle },
  ] : [];

  const adminContentItems = isAdmin ? [
    { id: "blog-management" as TabType, label: at("blogManagement", "ניהול בלוג"), icon: FileText },
    { id: "slider" as TabType, label: at("sliderManagementNav", "ניהול סליידר"), icon: Image },
    { id: "promo-banner" as TabType, label: at("promoBanner", "פס מבצעים"), icon: Megaphone },
    { id: "faq" as TabType, label: at("faq", "שאלות נפוצות"), icon: HelpCircle },
  ] : [];

  const adminOperationsItems = isAdmin ? [
    { id: "admin-repairs" as TabType, label: at("repairManagement", "ניהול תיקונים"), icon: Wrench },
    { id: "users" as TabType, label: at("userManagement", "ניהול משתמשים"), icon: Users },
  ] : [];

  const adminSettingsItems = isAdmin ? [
    { id: "settings" as TabType, label: at("menuSettings", "הגדרות תפריטים"), icon: Settings },
    { id: "search-settings" as TabType, label: at("searchSettings", "הגדרות חיפוש"), icon: Search },
    { id: "bot-settings" as TabType, label: at("botSettings", "הגדרות בוט"), icon: Bot },
    { id: "color-helper" as TabType, label: at("colorHelper", "עזרה - צבעים"), icon: Palette },
    { id: "accessibility" as TabType, label: at("accessibilityReport", "דוח נגישות"), icon: Accessibility },
  ] : [];

  const adminNavItems = [...adminCommunicationItems, ...adminContentItems, ...adminOperationsItems, ...adminSettingsItems];

  const getBadgeCount = (id: TabType) => {
    if (id === "repairs") return newRepairsCount;
    return 0;
  };

  return (
    <>
      {/* Fixed bottom navigation bar */}
      <nav className="fixed bottom-0 left-0 right-0 z-50 bg-background border-t border-border/50 safe-area-pb md:hidden">
        <div className="flex items-center justify-around h-16 px-2">
          {mainNavItems.map((item) => {
            const isActive = activeTab === item.id;
            const badgeCount = getBadgeCount(item.id);
            
            return (
              <button
                key={item.id}
                onClick={() => handleTabClick(item.id)}
                className={cn(
                  "flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors relative",
                  isActive ? "text-primary" : "text-muted-foreground"
                )}
              >
                <div className="relative">
                  <item.icon className="h-5 w-5" />
                  {item.hasBadge && badgeCount > 0 && (
                    <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium">
                      {badgeCount}
                    </span>
                  )}
                </div>
                <span className="text-[10px] font-medium">{item.label}</span>
                {isActive && (
                  <motion.div
                    layoutId="activeTab"
                    className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                  />
                )}
              </button>
            );
          })}

          {/* More button */}
          <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
            <SheetTrigger asChild>
              <button
                className={cn(
                  "flex flex-col items-center justify-center gap-1 flex-1 h-full transition-colors",
                  moreOpen ? "text-primary" : "text-muted-foreground"
                )}
              >
                <MoreHorizontal className="h-5 w-5" />
                <span className="text-[10px] font-medium">{at("more", "עוד")}</span>
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[280px] p-0" dir="rtl">
              <div className="flex flex-col h-full">
                {/* Header with greeting */}
                <div className="p-4 border-b border-border/50 bg-muted/30">
                  <p className="text-lg font-light text-foreground text-center">
                    {firstName ? `${at("hello", "שלום")} ${firstName}` : at("hello", "שלום")}
                  </p>
                </div>

                {/* Menu items */}
                <div className="flex-1 overflow-y-auto p-3 space-y-1">
                  {/* User section */}
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground px-2 mb-2">{at("moreOptions", "אפשרויות נוספות")}</p>
                    {moreNavItems.map((item) => (
                      <button
                        key={item.id}
                        onClick={() => handleMoreItemClick(item.id)}
                        className={cn(
                          "flex items-center gap-3 w-full py-2.5 px-3 rounded-lg transition-colors",
                          activeTab === item.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                        )}
                      >
                        <div className="relative">
                          <item.icon className="h-5 w-5" />
                          {item.badge && item.badge > 0 && (
                            <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium">
                              {item.badge}
                            </span>
                          )}
                        </div>
                        <span className="text-sm">{item.label}</span>
                      </button>
                    ))}
                  </div>

                  {/* Admin sections */}
                  {isAdmin && adminCommunicationItems.length > 0 && (
                    <div className="border-t border-border/50 pt-3">
                      <p className="text-xs text-muted-foreground px-2 mb-2">{at("communication", "תקשורת")}</p>
                      {adminCommunicationItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleMoreItemClick(item.id)}
                          className={cn(
                            "flex items-center gap-3 w-full py-2.5 px-3 rounded-lg transition-colors",
                            activeTab === item.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                          )}
                        >
                          <div className="relative">
                            <item.icon className="h-5 w-5" />
                            {'badge' in item && item.badge > 0 && (
                              <span className="absolute -top-1 -left-1 bg-destructive text-destructive-foreground text-[10px] rounded-full h-4 min-w-4 flex items-center justify-center px-0.5 font-medium">
                                {item.badge}
                              </span>
                            )}
                          </div>
                          <span className="text-sm">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {isAdmin && adminContentItems.length > 0 && (
                    <div className="border-t border-border/50 pt-3">
                      <p className="text-xs text-muted-foreground px-2 mb-2">{at("contentManagement", "ניהול תוכן")}</p>
                      {adminContentItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleMoreItemClick(item.id)}
                          className={cn(
                            "flex items-center gap-3 w-full py-2.5 px-3 rounded-lg transition-colors",
                            activeTab === item.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                          )}
                        >
                          <item.icon className="h-5 w-5" />
                          <span className="text-sm">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {isAdmin && adminOperationsItems.length > 0 && (
                    <div className="border-t border-border/50 pt-3">
                      <p className="text-xs text-muted-foreground px-2 mb-2">{at("operations", "תפעול")}</p>
                      {adminOperationsItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleMoreItemClick(item.id)}
                          className={cn(
                            "flex items-center gap-3 w-full py-2.5 px-3 rounded-lg transition-colors",
                            activeTab === item.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                          )}
                        >
                          <item.icon className="h-5 w-5" />
                          <span className="text-sm">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}

                  {isAdmin && adminSettingsItems.length > 0 && (
                    <div className="border-t border-border/50 pt-3">
                      <p className="text-xs text-muted-foreground px-2 mb-2">{at("settingsAndTools", "הגדרות וכלים")}</p>
                      {adminSettingsItems.map((item) => (
                        <button
                          key={item.id}
                          onClick={() => handleMoreItemClick(item.id)}
                          className={cn(
                            "flex items-center gap-3 w-full py-2.5 px-3 rounded-lg transition-colors",
                            activeTab === item.id ? "bg-primary/10 text-primary" : "hover:bg-muted/50"
                          )}
                        >
                          <item.icon className="h-5 w-5" />
                          <span className="text-sm">{item.label}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer actions */}
                <div className="p-3 border-t border-border/50 space-y-2">
                  <RouterLink
                    to="/"
                    onClick={() => setMoreOpen(false)}
                    className="flex items-center gap-3 w-full py-2.5 px-3 rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <Home className="h-5 w-5 text-foreground" />
                    <span className="text-sm">{at("backToSite", "חזרה לאתר")}</span>
                  </RouterLink>
                  <button
                    onClick={() => {
                      setMoreOpen(false);
                      setTimeout(onLogout, 100);
                    }}
                    className="flex items-center gap-3 w-full py-2.5 px-3 rounded-lg hover:bg-destructive/10 transition-colors text-destructive"
                  >
                    <LogOut className="h-5 w-5" />
                    <span className="text-sm">{at("disconnect", "התנתק")}</span>
                  </button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>

      {/* Spacer to prevent content from being hidden behind bottom nav */}
      <div className="h-16 md:hidden" />
    </>
  );
};
